<template>
  <AIVDialog
    v-model="isVisible"
    :title="viewOnly ? 'Shift Details' : 'Shift Actions'"
    width="40%"
    height="85vh"
    class="mobile-friendly-modal shift-action-modal"
  >
    <div class="shift-action-content">
      <!-- Shift Info Display -->
      <div class="mb-3">
        <h5>Shift Details</h5>
        <p><strong>Date:</strong> {{ shift && shift.date ? formatDate(shift.date) : 'N/A' }}</p>
        <p
          v-if="
            shift &&
            (shift.originalData?.clockIn ||
              shift.originalData?.clock_in ||
              shift.originalData?.clockOut ||
              shift.originalData?.clock_out ||
              shift.actualStartTime ||
              shift.actualEndTime) &&
            (shift.status === 'approved' || shift.status === 'clocked-out' || shift.status === 'clocked out' || shift.status === 'error')
          "
        >
          <strong>Actual Time:</strong>
          {{ formatActualTimeRange(shift) }}
        </p>
        <p
          v-if="
            shift &&
            hasCompletedBreaks(shift) &&
            (shift.status === 'approved' || shift.status === 'clocked-out' || shift.status === 'clocked out' || shift.status === 'error')
          "
        >
          <strong>Break Time:</strong>
          {{ formatBreakTimeRange(shift) }}
        </p>
        <p
          v-if="
            shift &&
            (shift.originalData?.clockIn ||
              shift.originalData?.clock_in ||
              shift.originalData?.clockOut ||
              shift.originalData?.clock_out ||
              shift.actualStartTime ||
              shift.actualEndTime) &&
            (shift.status === 'approved' || shift.status === 'clocked-out' || shift.status === 'clocked out' || shift.status === 'error')
          "
        >
          <strong>Hours Worked:</strong>
          {{ calculateHoursWorked(shift) }}
        </p>
        <p v-if="shiftMiles !== null">
          <strong>Mileage:</strong>
          {{ shiftMiles.toFixed(1) }} miles
        </p>
        <p>
          <strong>Scheduled Time:</strong>
          {{ formatTimeRange(
            shift?.scheduledStartTime || shift?.originalData?.scheduled_start || shift?.startTime,
            shift?.scheduledEndTime || shift?.originalData?.scheduled_end || shift?.endTime,
            shift?.date
          ) }}
        </p>
        <p
          v-if="
            shift &&
            (shift.status === 'clocked-in' || shift.status === 'clocked in' ||
             shift.status === 'clocked-out' || shift.status === 'clocked out' ||
             shift.status === 'missed' || shift.status === 'approved')
          "
        >
          <strong>Hours Scheduled:</strong>
          {{ calculateHoursScheduled(shift) }}
        </p>
        <p v-if="hideField !== 'client' || isGroupShift">
          <strong>{{ isGroupShift ? 'Clients:' : 'Client:' }}</strong>
          <template v-if="clientLinks.length">
            <template v-for="(c, i) in clientLinks"><router-link
                v-if="c.id"
                :key="'client-link-' + i"
                :to="`/pages/clients/${c.id}`"
              >{{ c.name }}</router-link><span v-else :key="'client-text-' + i">{{ c.name }}</span><span
                v-if="i < clientLinks.length - 1"
                :key="'client-sep-' + i"
              >, </span></template>
          </template>
          <template v-else>{{ shift ? formatClient(shift) : 'N/A' }}</template>
        </p>
        <p v-if="clientLocationInfo && shift?.payer !== 'Non Service' && !isGroupShift">
          <strong>Client Address:</strong> {{ clientLocationInfo.address }}
          <el-button
            type="text"
            size="small"
            @click="openMapLinkFromAddress(clientLocationInfo.address)"
            class="map-link-btn"
          >
            <i class="fa-solid fa-location-dot"></i> View on Map
          </el-button>
        </p>
        <p v-if="hideField !== 'caregiver' || isGroupShift">
          <strong>{{ isGroupShift || caregiverLinks.length > 1 ? 'Caregivers:' : 'Caregiver:' }}</strong>
          <template v-if="caregiverLinks.length">
            <template v-for="(cg, i) in caregiverLinks"><router-link
                v-if="cg.id"
                :key="'caregiver-link-' + i"
                :to="`/pages/caregivers/${cg.id}`"
              >{{ cg.name }}</router-link><span v-else :key="'caregiver-text-' + i">{{ cg.name }}</span><span
                v-if="i < caregiverLinks.length - 1"
                :key="'caregiver-sep-' + i"
              >, </span></template>
          </template>
          <template v-else>{{ shift ? formatCaregiver(shift) : 'N/A' }}</template>
        </p>
        <div v-if="shiftNotes" class="shift-notes-section">
          <strong><i class="fa-solid fa-sticky-note"></i> Notes:</strong>
          <div class="shift-notes-content">{{ shiftNotes }}</div>
        </div>
        <div v-if="shiftNoteAnswers.length" class="shift-questions-section">
          <strong><i class="fa-solid fa-clipboard-question"></i> Shift Question Results:</strong>
          <div
            v-for="a in shiftNoteAnswers"
            :key="'ans-' + a.id"
            class="shift-question-item"
          >
            <div class="shift-question-text">{{ a.question_text }}</div>
            <div class="shift-question-answer">{{ formatAnswer(a) }}</div>
          </div>
        </div>
        <p>
          <strong>Status:</strong>
          <span v-if="shift && shift.status" :class="['status-badge', getStatusClass(shift.status)]">{{
            formatStatus(shift.status)
          }}</span>
          <span v-else>N/A</span>
        </p>
        <div v-if="shift && shift.status === 'canceled' && cancelReason" class="cancel-reason-box">
          <p><strong>Cancellation Reason:</strong></p>
          <p class="cancel-reason-text">{{ cancelReason }}</p>
        </div>
      </div>
    </div>

    <template slot="footer">
      <div class="text-center">
        <el-button @click="handleCancel">{{
          viewOnly ? "Close" : "Cancel"
        }}</el-button>
        <el-button
          v-if="!viewOnly && canClockIntoShift"
          type="warning"
          class="clock-in-btn"
          @click="handleClockIn"
        >
          <i class="fa-solid fa-clock"></i> Clock In
        </el-button>
        <el-button
          v-if="!viewOnly && showTasksButton"
          type="primary"
          @click="handleOpenTasks"
        >
          <i class="fa-solid fa-list-check"></i> Tasks
        </el-button>
        <el-button
          v-if="!viewOnly && canClockOutOfShift"
          type="warning"
          class="clock-out-btn"
          @click="handleClockOut"
        >
          <i class="fa-solid fa-clock"></i> Clock Out
        </el-button>
        <el-button
          v-if="!viewOnly && canAdminClockOut"
          type="warning"
          class="admin-clock-out-btn"
          @click="handleAdminClockOut"
        >
          <i class="fa-solid fa-user-clock"></i> Admin Clock Out
        </el-button>
        <el-button
          v-if="!viewOnly && canStartBreak"
          type="warning"
          @click="handleStartBreak"
          class="break-btn"
        >
          <i :class="isOnBreak ? 'fa-solid fa-play' : 'fa-solid fa-pause'"></i>
          {{ isOnBreak ? 'End Break' : 'Start Break' }}
        </el-button>
        <el-button
          v-if="!viewOnly && canEditShift"
          type="primary"
          @click="handleEditShift"
        >
          <i class="fa-solid fa-edit"></i> Edit Shift
        </el-button>
        <el-button
          v-if="canEditShiftNotes"
          type="primary"
          @click="handleEditShiftNotes"
        >
          <i class="fa-solid fa-edit"></i> Edit
        </el-button>
        <el-button
          v-if="!viewOnly && canEditClockInTime"
          type="warning"
          @click="handleEditClockInTime"
        >
          <i class="fa-solid fa-clock-rotate-left"></i> Edit Clock In Time
        </el-button>
        <el-button
          v-if="!viewOnly && canEditClockOutTime"
          type="warning"
          @click="handleEditClockOutTime"
        >
          <i class="fa-solid fa-clock-rotate-left"></i> Edit Clock Times
        </el-button>
        <el-button
          v-if="!viewOnly && canApproveShift"
          type="success"
          @click="handleApproveShift"
        >
          <i class="fa-solid fa-check-circle"></i> Approve
        </el-button>
        <el-button
          v-if="!viewOnly && canUndoApproval"
          type="danger"
          @click="handleUndoApproval"
        >
          <i class="fa-solid fa-times-circle"></i> Undo Approval
        </el-button>
        <el-button
          v-if="!viewOnly && canApproveMissedShift"
          type="success"
          @click="handleApproveMissedShift"
        >
          <i class="fa-solid fa-check-circle"></i> Approve Missed Shift
        </el-button>
        <el-button
          v-if="canCancelShift"
          type="danger"
          @click="handleCancelShift"
        >
          <i class="fa-solid fa-ban"></i> Cancel Shift
        </el-button>
        <el-button
          v-if="canDeleteShift"
          type="danger"
          @click="handleDeleteShift"
        >
          <i class="fa-solid fa-trash"></i> Delete Shift
        </el-button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import AIVDialog from "@/components/AIVDialog.vue";
import { useLoginStore } from "@/stores/loginStore";
import { isDSPDGroupShift, getCaregiverShiftStatus, areAllGroupTasksComplete, caregiverNameWithRole } from "@/utils/shiftStatus";
import { milesForCaregiver, totalShiftMiles } from "@/utils/shiftMiles";
import { shiftsService } from "@/serviceHandlers/shiftsService";

export default {
  name: "ShiftActionModal",
  components: { AIVDialog },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    shift: {
      type: Object,
      default: () => ({}),
    },
    viewOnly: {
      type: Boolean,
      default: false,
    },
    hideField: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      shiftNoteAnswers: [],
    };
  },

  computed: {
    isVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },

    isGroupShift() {
      if (!this.shift) return false;
      const shiftData = this.shift.originalData || this.shift;
      return shiftData.shift_type === 'Group' || this.shift.shiftType === 'Group';
    },

    // Recorded miles, viewer-scoped: caregivers see their own trip,
    // everyone else sees all trips recorded on the shift.
    shiftMiles() {
      if (!this.shift) return null;
      const shiftData = this.shift.originalData || this.shift;
      const user = useLoginStore().user;
      if (user?.user_type === "Caregiver") {
        return milesForCaregiver(shiftData, user.id);
      }
      return totalShiftMiles(shiftData);
    },

    shiftNotes() {
      if (!this.shift) return "";
      const shiftData = this.shift.originalData || this.shift;
      const raw =
        this.shift.shift_notes ||
        this.shift.notes ||
        shiftData.shift_notes ||
        shiftData.notes ||
        "";
      return this.dedupeNoteText(raw);
    },

    // Clients on the shift as { id, name } for linking to their detail pages.
    clientLinks() {
      if (!this.shift) return [];
      const clients = this.shift.clients || this.shift.originalData?.clients || [];
      return clients.map((client) => ({
        id: client.id || null,
        name:
          (client.name ||
            `${client.first_name || ""} ${client.last_name || ""}`.trim()) ||
          "Unnamed Client",
      }));
    },

    // Caregivers on the shift as { id, name } for linking to their detail pages.
    caregiverLinks() {
      if (!this.shift) return [];
      const caregivers =
        this.shift.caregivers || this.shift.originalData?.caregivers || [];
      return caregivers.map((caregiver) => ({
        id: caregiver.id || null,
        name:
          caregiverNameWithRole(caregiver) || "Unnamed Caregiver",
      }));
    },

    clientLocationInfo() {
      if (!this.shift) return null;
      const shiftData = this.shift.originalData || this.shift;

      let location = null;

      if (shiftData.use_separate_clock_addresses && shiftData.clockInAddress?.address1) {
        location = {
          address1: shiftData.clockInAddress.address1,
          address2: shiftData.clockInAddress.address2,
          city: shiftData.clockInAddress.city,
          state: shiftData.clockInAddress.state,
          zip: shiftData.clockInAddress.zip,
        };
      } else if (shiftData.alternateAddress?.address1) {
        location = {
          address1: shiftData.alternateAddress.address1,
          address2: shiftData.alternateAddress.address2,
          city: shiftData.alternateAddress.city,
          state: shiftData.alternateAddress.state,
          zip: shiftData.alternateAddress.zip,
        };
      } else {
        const clients = shiftData.clients || this.shift.clients || [];
        if (clients.length > 0) {
          const client = clients[0];
          if (client.addresses && Array.isArray(client.addresses) && client.addresses.length > 0) {
            const firstAddress = client.addresses[0];
            location = {
              address1: firstAddress.address1 || firstAddress.street || firstAddress.address,
              address2: firstAddress.address2,
              city: firstAddress.city,
              state: firstAddress.state,
              zip: firstAddress.zip || firstAddress.postal_code,
            };
          } else if (client.address && typeof client.address === 'object') {
            location = {
              address1: client.address.address1 || client.address.street || client.address.address,
              address2: client.address.address2,
              city: client.address.city,
              state: client.address.state,
              zip: client.address.zip || client.address.postal_code,
            };
          } else if (typeof client.address === 'string') {
            location = { address1: client.address };
          }
        }
      }

      if (!location) return null;
      const parts = [];
      if (location.address1) parts.push(location.address1);
      if (location.address2) parts.push(location.address2);
      if (location.city) parts.push(location.city);
      if (location.state) parts.push(location.state);
      if (location.zip) parts.push(location.zip);
      const address = parts.join(', ');
      return address ? { address } : null;
    },

    userRole() {
      const loginStore = useLoginStore();
      return loginStore.user?.user_type || "Admin";
    },

    userId() {
      const loginStore = useLoginStore();
      return loginStore.user?.id;
    },

    // True when an admin/lead is also one of the assigned caregivers on a DSPD group shift.
    // In this case they should see the caregiver view, not the admin view.
    isAssignedCaregiverOnGroupShift() {
      if (!this.shift) return false;
      const role = this.userRole;
      if (role !== "Admin" && role !== "Lead") return false;
      const shiftData = this.shift.originalData || this.shift;
      if (!isDSPDGroupShift(shiftData)) return false;
      const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
      return caregivers.some(c => Number(c.id) === Number(this.userId));
    },

    canEditShift() {
      if (!this.shift) return false;
      const role = this.userRole;
      const status = this.shift.status;

      // Admins can edit group shifts in clocked-out status
      // (clocked-in group shifts use the "Edit Clock In Time" button instead)
      if (this.isGroupShift && role === "Admin") {
        if (status === "clocked-out" || status === "clocked out") {
          return true;
        }
      }

      // Don't show edit button if shift is approved or clocked in
      if (
        status === "approved" ||
        status === "clocked-in" ||
        status === "clocked in"
      ) {
        return false;
      }

      // For group shifts where the current user is an assigned caregiver,
      // the status reflects their INDIVIDUAL status (e.g. "scheduled") even if
      // another caregiver has already clocked in. Check caregiverClockTimes directly.
      if (this.isAssignedCaregiverOnGroupShift) {
        const shiftData = this.shift.originalData || this.shift;
        const clockTimes = shiftData.caregiverClockTimes || [];
        const anyClockedIn = clockTimes.some(ct => ct.clock_in);
        if (anyClockedIn) {
          return false;
        }
      }

      // For clocked-out/error shifts, allow edit for assigned caregivers (any role)
      // Non-assigned admins use the "Edit Clock Times" button instead
      // Group shifts are excluded - only admins edit group shifts (handled above)
      if (status === "clocked-out" || status === "clocked out" || status === "error") {
        if (this.isGroupShift) return false;
        const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
        return caregivers.some(c => Number(c.id) === Number(this.userId));
      }

      // For missed shifts, allow Admins, Leads, Caregivers, and Employment Specialists to edit
      if (status === "missed") {
        if (role === "Admin" || role === "Lead") {
          return true;
        }

        // Caregivers and Employment Specialists can edit their own missed shifts
        if (role === "Caregiver" || role === "Employment Specialist") {
          const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
          const caregiver = caregivers.length > 0 ? caregivers[0] : null;
          if (caregiver && caregiver.id === this.userId) {
            return true;
          }
        }

        return false;
      }

      return (
        role === "Admin" || role === "Lead" || role === "Employment Specialist"
      );
    },

    canEditShiftNotes() {
      if (!this.shift) return false;
      // Don't show if the regular Edit Shift button is already visible
      if (this.canEditShift) return false;
      // Group shifts don't use the notes edit button - tasks are managed separately
      if (this.isGroupShift) return false;

      const status = this.shift.status;

      // Only show for statuses where shift notes exist (not while clocked in)
      const editableStatuses = [
        'clocked-out', 'clocked out',
        'error', 'missed', 'approved',
      ];
      if (!editableStatuses.includes(status)) return false;

      // Admins may edit documentation (question answers, notes, activities)
      // on approved shifts — the notes modal locks payer/client/service code
      // for approved shifts. Parity with the Shifts page.
      if (status === 'approved' && this.userRole === 'Admin') return true;

      // Must be an assigned caregiver on this shift
      const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
      return caregivers.some(c => Number(c.id) === Number(this.userId));
    },

    canEditClockInTime() {
      if (!this.shift) return false;
      // Admin acting as caregiver on group shift → caregiver view only
      if (this.isAssignedCaregiverOnGroupShift) return false;
      const role = this.userRole;
      const status = this.shift.status;

      // Only Admins can edit clock times
      if (role !== "Admin") return false;

      // Check if shift is in the correct status
      const isCorrectStatus = (status === "clocked-in" || status === "clocked in");
      if (!isCorrectStatus) return false;

      // Check both possible locations for caregivers data
      const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
      const caregiver = caregivers.length > 0 ? caregivers[0] : null;

      // Admins cannot edit their own clock times
      if (caregiver && caregiver.id === this.userId) {
        return false;
      }

      return true;
    },

    canEditClockOutTime() {
      if (!this.shift) return false;
      // Admin acting as caregiver on group shift → caregiver view only
      if (this.isAssignedCaregiverOnGroupShift) return false;
      const role = this.userRole;
      const status = this.shift.status;

      // Only Admins can edit clock times
      if (role !== "Admin") return false;

      // Check if shift is in the correct status
      const isCorrectStatus = (status === "clocked-out" || status === "clocked out" || status === "error");
      if (!isCorrectStatus) return false;

      // Check both possible locations for caregivers data
      const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
      const caregiver = caregivers.length > 0 ? caregivers[0] : null;

      // Admins cannot edit their own clock times
      if (caregiver && caregiver.id === this.userId) {
        return false;
      }

      return true;
    },

    canApproveShift() {
      if (!this.shift) return false;
      // Admin acting as caregiver on group shift → caregiver view only
      if (this.isAssignedCaregiverOnGroupShift) return false;
      const role = this.userRole;
      const status = this.shift.status;

      // Only admins can approve when shift is clocked out, but NOT when status is error
      const isClockedOut = status === "clocked-out" || status === "clocked out";
      if (!isClockedOut || status === "error") {
        return false;
      }

      // Only Admins can approve shifts
      if (role !== "Admin") return false;

      // Check both possible locations for caregivers data
      const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
      if (caregivers.length === 0) return false;

      // Admins cannot approve a shift where they are an assigned caregiver
      if (caregivers.some(c => Number(c.id) === Number(this.userId))) {
        return false;
      }

      return true;
    },

    canUndoApproval() {
      if (!this.shift) return false;
      // Admin acting as caregiver on group shift → caregiver view only
      if (this.isAssignedCaregiverOnGroupShift) return false;
      const role = this.userRole;
      const status = this.shift.status;

      // Only admins can undo approval for approved shifts
      if (status !== "approved") return false;

      // Only Admins can undo shift approvals
      if (role !== "Admin") return false;

      return true;
    },

    canApproveMissedShift() {
      if (!this.shift) return false;
      // Admin acting as caregiver on group shift → caregiver view only
      if (this.isAssignedCaregiverOnGroupShift) return false;
      const role = this.userRole;
      const status = this.shift.status;

      // Only admins can approve missed shifts
      if (status !== "missed") return false;

      // Only Admins can approve shifts
      if (role !== "Admin") return false;

      // Check both possible locations for caregivers data
      const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
      if (caregivers.length === 0) return false;

      // Admins cannot approve a shift where they are an assigned caregiver
      if (caregivers.some(c => Number(c.id) === Number(this.userId))) {
        return false;
      }

      return true;
    },

    cancelReason() {
      if (!this.shift) return null;
      return this.shift.originalData?.cancel_reason || this.shift.cancel_reason || null;
    },

    canCancelShift() {
      if (!this.shift || this.viewOnly) return false;
      const role = this.userRole;
      if (role !== "Admin") return false;
      const status = this.shift.status;
      // Can cancel shifts that are Unassigned, Scheduled, or Missed
      return ["unassigned", "scheduled", "missed"].includes(status);
    },

    // Mirrors canDeleteShift in Shifts.vue so the calendar offers the same
    // delete permissions as the shifts page.
    canDeleteShift() {
      if (!this.shift) return false;
      const status = (this.shift.status || this.shift.shift_status || "").toLowerCase();
      const role = this.userRole;

      // For clocked in/out shifts, only Admins can delete
      if (
        status === "clocked-in" ||
        status === "clocked in" ||
        status === "clocked_in" ||
        status === "clocked-out" ||
        status === "clocked out" ||
        status === "clocked_out"
      ) {
        return role === "Admin";
      }

      // Cannot delete approved or error shifts
      if (status === "approved" || status === "error") {
        return false;
      }

      // For canceled shifts, only Admins can delete
      if (status === "canceled") {
        return role === "Admin";
      }

      // For missed shifts, only Admins and Leads can delete
      if (status === "missed") {
        return role === "Admin" || role === "Lead";
      }

      // For other statuses, use same permissions as edit
      return this.canEditShift;
    },

    canClockIntoShift() {
      if (!this.shift || !this.shift.id) {
        return false;
      }

      const status = this.shift.status || this.shift.shift_status;

      // Check both possible locations for caregivers data
      const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
      const isLinkedToUser = caregivers.some(
        (c) =>
          Number(c.id) === Number(this.userId) ||
          String(c.id) === String(this.userId),
      );

      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const shiftDateStr = this.shift.date.split("T")[0];
      const isToday = todayStr === shiftDateStr;

      const now = new Date();
      const currentTimeMinutes = now.getHours() * 60 + now.getMinutes();

      const startTime =
        this.shift.originalData?.scheduled_start || this.shift.scheduled_start || this.shift.startTime || "";
      const endTime =
        this.shift.originalData?.scheduled_end || this.shift.scheduled_end || this.shift.endTime || "";

      if (startTime && endTime) {
        const convertUTCTimeToLocalMinutes = (timeStr, dateStr) => {
          // Handle full ISO datetime strings (e.g., "2025-09-23T13:00:00.000Z")
          if (timeStr && timeStr.includes('T')) {
            const utcDate = new Date(timeStr);
            if (!isNaN(utcDate.getTime())) {
              const result = utcDate.getHours() * 60 + utcDate.getMinutes();
              return result;
            }
          }

          // Handle time-only strings (e.g., "13:00:00")
          const localDateTime = `${dateStr}T${timeStr}`;
          const utcDate = new Date(localDateTime);
          if (!isNaN(utcDate.getTime())) {
            const result = utcDate.getHours() * 60 + utcDate.getMinutes();
            return result;
          }

          // Fallback: parse as HH:MM
          const [hour, min] = timeStr.split(":").map(Number);
          const result = hour * 60 + min;
          return result;
        };

        const startMinutes = convertUTCTimeToLocalMinutes(
          startTime,
          shiftDateStr,
        );
        const endMinutes = convertUTCTimeToLocalMinutes(endTime, shiftDateStr);
        // CMP exception: allow clock-in any time during the calendar day
        const serviceCodes = this.shift.service_codes || this.shift.originalData?.service_codes || [];
        const isCMPShift = serviceCodes.some(sc => sc.code === 'CMP');

        const allowedStartMinutes = startMinutes - 30; // 30 minutes before

        const isInTimeWindow = isCMPShift
          ? true // CMP shifts can clock in any time on the shift's calendar day
          : (currentTimeMinutes >= allowedStartMinutes &&
             currentTimeMinutes < endMinutes);

        // For DSPD group shifts, check the individual caregiver's status
        // (the overall shift status may not be "scheduled" if another caregiver already clocked in)
        const shiftData = this.shift.originalData || this.shift;
        let isScheduledStatus;
        if (isDSPDGroupShift(shiftData)) {
          const caregiverStatus = getCaregiverShiftStatus(shiftData, this.userId);
          isScheduledStatus = caregiverStatus === 'scheduled';
        } else {
          isScheduledStatus = status && (status.toLowerCase() === "scheduled");
        }
        const canClock = isScheduledStatus && isLinkedToUser && isToday && isInTimeWindow && this.canClockIntoShiftForGroup;

        return canClock;
      }

      return false;
    },

    showTasksButton() {
      if (!this.shift || !this.shift.id) return false;
      const shiftData = this.shift.originalData || this.shift;
      if (!isDSPDGroupShift(shiftData)) return false;
      const status = getCaregiverShiftStatus(shiftData, this.userId);
      return status === 'clocked-in';
    },

    canClockOutOfShift() {
      if (!this.shift || !this.shift.id) return false;

      const status = this.shift.status;
      const isLinkedToUser = this.shift.originalData?.caregivers?.some(
        (c) =>
          Number(c.id) === Number(this.userId) ||
          String(c.id) === String(this.userId),
      );

      // For DSPD group shifts, only show clock out when all tasks are complete
      const shiftData = this.shift.originalData || this.shift;
      if (isDSPDGroupShift(shiftData)) {
        const caregiverStatus = getCaregiverShiftStatus(shiftData, this.userId);
        return caregiverStatus === 'clocked-in' && isLinkedToUser && areAllGroupTasksComplete(shiftData, this.userId);
      }

      // Only show clock out if shift is clocked in and user is the assigned caregiver
      return (
        (status === "clocked-in" || status === "clocked in") && isLinkedToUser
      );
    },

    // Admin/Lead override: close a shift on behalf of a caregiver who forgot to
    // clock out. Hidden when the normal caregiver Clock Out button already shows
    // (i.e. when the admin is themselves the assigned, clocked-in caregiver).
    canAdminClockOut() {
      if (!this.shift || !this.shift.id) return false;
      const role = this.userRole;
      if (role !== "Admin" && role !== "Lead") return false;
      if (this.canClockOutOfShift) return false;

      const shiftData = this.shift.originalData || this.shift;
      if (isDSPDGroupShift(shiftData)) {
        // Any caregiver still on the clock (clocked in, no clock out yet).
        const clockTimes = shiftData.caregiverClockTimes || [];
        return clockTimes.some((ct) => ct.clock_in && !ct.clock_out);
      }

      const status = this.shift.status;
      return status === "clocked-in" || status === "clocked in";
    },

    canClockIntoShiftForGroup() {
      // For DSPD group shifts, hide clock-in once this caregiver is clocked in
      if (!this.shift || !this.shift.id) return true;
      const shiftData = this.shift.originalData || this.shift;
      if (!isDSPDGroupShift(shiftData)) return true;
      const status = getCaregiverShiftStatus(shiftData, this.userId);
      return status === 'scheduled';
    },

    canStartBreak() {
      if (!this.shift || !this.shift.id) return false;

      // No breaks for DSPD group shifts
      const shiftData = this.shift.originalData || this.shift;
      if (isDSPDGroupShift(shiftData)) return false;

      // Must be clocked in
      const status = this.shift.status;
      if (status !== "clocked-in" && status !== "clocked in") {
        return false;
      }

      // Must be assigned to this shift
      const caregivers = this.shift.originalData?.caregivers || this.shift.caregivers || [];
      const isLinkedToUser = caregivers.some(
        (c) => Number(c.id) === Number(this.userId) || String(c.id) === String(this.userId),
      );
      if (!isLinkedToUser) {
        return false;
      }

      // Must have JC service code
      const serviceCodes = this.shift.service_codes || this.shift.originalData?.service_codes || [];
      const hasJCServiceCode = serviceCodes.some(sc => sc.code === 'JC');
      if (!hasJCServiceCode) {
        return false;
      }

      // Must be >= 4 hours
      const startTime = this.shift.scheduledStartTime || this.shift.originalData?.scheduled_start || this.shift.startTime;
      const endTime = this.shift.scheduledEndTime || this.shift.originalData?.scheduled_end || this.shift.endTime;

      if (!startTime || !endTime) {
        return false;
      }

      // Handle both full ISO datetime strings and time-only strings
      let startDate, endDate;
      if (startTime.includes('T')) {
        // Full ISO datetime string
        startDate = new Date(startTime);
        endDate = new Date(endTime);
      } else {
        // Time-only string, combine with date
        const dateStr = this.shift.date?.split('T')[0];
        if (!dateStr) return false;
        startDate = new Date(`${dateStr}T${startTime}`);
        endDate = new Date(`${dateStr}T${endTime}`);
      }

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return false;
      }

      const durationHours = (endDate - startDate) / (1000 * 60 * 60);
      if (durationHours < 4) {
        return false;
      }

      // Check if a break has already been completed
      // If there's any break with both break_start and break_end, don't show button
      const breaks = this.shift.breaks || this.shift.originalData?.breaks || [];
      const hasCompletedBreak = breaks.some(b => b.break_start && b.break_end);
      if (hasCompletedBreak) {
        return false;
      }

      return true;
    },

    isOnBreak() {
      if (!this.shift) return false;

      // Use the isOnBreak flag from the API response
      if (this.shift.isOnBreak !== undefined) {
        return this.shift.isOnBreak;
      }

      // Check if there's an active break in the breaks array
      if (this.shift.breaks && this.shift.breaks.length > 0) {
        return this.shift.breaks.some(b => b.break_start && !b.break_end);
      }

      // Check originalData for breaks
      if (this.shift.originalData?.breaks && this.shift.originalData.breaks.length > 0) {
        return this.shift.originalData.breaks.some(b => b.break_start && !b.break_end);
      }

      // Legacy fallback: Check if there's an active break (break_start exists but break_end doesn't)
      return this.shift.break_start && !this.shift.break_end;
    },
  },

  watch: {
    shift: {
      handler(newShift, oldShift) {
        // Force re-evaluation of computed properties when shift changes
        if (newShift) {
          this.$nextTick(() => {
            // Trigger computed property re-evaluation
            this.canClockIntoShift;
          });
        }
      },
      immediate: true
    },
    visible(val) {
      if (val) {
        this.fetchShiftNoteAnswers();
      } else {
        this.shiftNoteAnswers = [];
      }
    },
    "shift.id"() {
      if (this.visible) {
        this.fetchShiftNoteAnswers();
      }
    },
  },

  methods: {
    // Collapse duplicate note blocks (split on blank lines) that result from
    // legacy double-saves, mirroring the backend dedupe in clientNotes.
    dedupeNoteText(text) {
      if (!text || typeof text !== "string") return text || "";
      const blocks = text
        .split(/\n\s*\n/)
        .map((b) => b.trim())
        .filter(Boolean);
      const unique = [];
      for (const b of blocks) {
        if (!unique.includes(b)) unique.push(b);
      }
      return unique.join("\n\n");
    },
    async fetchShiftNoteAnswers() {
      this.shiftNoteAnswers = [];
      const shiftId = this.shift && this.shift.id;
      const status = this.shift && this.shift.status;
      const completed =
        status === "clocked-out" ||
        status === "clocked out" ||
        status === "approved" ||
        status === "missed" ||
        status === "error";
      if (!shiftId || !completed) return;
      try {
        const answers = await shiftsService.getNoteAnswers(shiftId);
        this.shiftNoteAnswers = Array.isArray(answers) ? answers : [];
      } catch (e) {
        this.shiftNoteAnswers = [];
      }
    },
    formatAnswer(a) {
      if (a.answer === "yes") return "Yes";
      if (a.answer === "no") return "No";
      return a.answer;
    },
    openMapLinkFromAddress(address) {
      const encodedAddress = encodeURIComponent(address);
      const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
      window.open(mapUrl, '_blank');
    },

    handleOpenTasks() {
      this.$emit("open-tasks", this.shift);
      this.isVisible = false;
    },

    handleClockIn() {
      this.$emit("clock-in", this.shift);
      this.isVisible = false;
    },

    handleClockOut() {
      this.$emit("clock-out", this.shift);
      this.isVisible = false;
    },

    handleAdminClockOut() {
      this.$emit("admin-clock-out", this.shift);
      this.isVisible = false;
    },

    handleStartBreak() {
      this.$emit("start-break", this.shift);
      this.isVisible = false;
    },

    handleEditShift() {
      this.$emit("edit-shift", this.shift);
      this.isVisible = false;
    },

    handleEditShiftNotes() {
      this.$emit("edit-shift-notes", this.shift);
      this.isVisible = false;
    },

    handleEditClockInTime() {
      this.$emit("edit-clock-in-time", this.shift);
      this.isVisible = false;
    },

    handleEditClockOutTime() {
      this.$emit("edit-clock-out-time", this.shift);
      this.isVisible = false;
    },

    handleApproveShift() {
      this.$emit("approve-shift", this.shift);
      this.isVisible = false;
    },

    handleCancelShift() {
      this.$emit("cancel-shift", this.shift);
      this.isVisible = false;
    },

    handleDeleteShift() {
      this.$emit("delete-shift", this.shift);
      this.isVisible = false;
    },

    handleUndoApproval() {
      this.$emit("undo-approval", this.shift);
      this.isVisible = false;
    },

    handleApproveMissedShift() {
      this.$emit("approve-missed-shift", this.shift);
      this.isVisible = false;
    },

    handleCancel() {
      this.isVisible = false;
    },

    convertUTCDateTimeToLocalTime(utcDateTimeStr) {
      if (!utcDateTimeStr) return "";

      // Parse the datetime string - it should already be a proper UTC timestamp from backend
      const utcDate = new Date(utcDateTimeStr);
      if (isNaN(utcDate.getTime())) return "";

      // Convert to local time string (HH:MM format)
      return utcDate.toLocaleTimeString("en-US", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
      });
    },

    formatClockInTime(dateTimeString) {
      if (!dateTimeString) return "";

      // Use the same conversion logic to get local time
      const localTime = this.convertUTCDateTimeToLocalTime(dateTimeString);

      // Convert HH:MM to 12-hour format for display
      if (!localTime) return "";

      const [hours, minutes] = localTime.split(":").map(Number);
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;

      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    },

    formatClockOutTime(dateTimeString) {
      if (!dateTimeString) return "";

      // Use the same conversion logic to get local time
      const localTime = this.convertUTCDateTimeToLocalTime(dateTimeString);

      // Convert HH:MM to 12-hour format for display
      if (!localTime) return "";

      const [hours, minutes] = localTime.split(":").map(Number);
      const ampm = hours >= 12 ? "PM" : "AM";
      const displayHours = hours % 12 || 12;

      return `${displayHours}:${minutes.toString().padStart(2, "0")} ${ampm}`;
    },

    formatTime(timeString) {
      if (!timeString) return "";

      const timeParts = timeString.split(":");
      let hours = parseInt(timeParts[0]);
      const minutes = timeParts[1] || "00";

      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;

      const formattedMinutes = minutes === "00" ? "00" : minutes;
      return `${hours}:${formattedMinutes} ${ampm}`;
    },

    formatDate(dateString) {
      if (!dateString) return "";
      // Handle date string in YYYY-MM-DD format as local date
      // to avoid timezone conversion issues
      let date;
      if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
        // Parse as local date by adding time component
        date = new Date(dateString + 'T00:00:00');
      } else {
        // For other formats, use default parsing
        date = new Date(dateString);
      }
      return date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    },

    formatScheduledTime(timeStr, date = null) {
      if (!timeStr) return "N/A";

      // If timeStr is already a full datetime (ISO string), use it directly
      if (timeStr.includes('T')) {
        const utcDate = new Date(timeStr);
        if (!isNaN(utcDate.getTime())) {
          return utcDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Legacy: If we have a date, combine it with the time and treat as local time
      if (date) {
        const dateOnly = date.split('T')[0];
        const localDateTime = `${dateOnly}T${timeStr}`;
        const localDate = new Date(localDateTime);

        if (!isNaN(localDate.getTime())) {
          return localDate.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          });
        }
      }

      // Fallback: treat as local time
      return this.formatTime12Hour(timeStr);
    },

    formatTime12Hour(timeStr) {
      if (!timeStr) return "";
      const [hour, minute] = timeStr.split(":").map(Number);
      const period = hour >= 12 ? "PM" : "AM";
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
    },

    formatTimeRange(startTime, endTime, date = null) {
      // Handle new UTC datetime format (with 'T' in the string)
      if (startTime && startTime.includes('T')) {
        const start = this.formatScheduledTime(startTime);
        const end = this.formatScheduledTime(endTime);
        return `${start} - ${end}`;
      }

      // Legacy: handle old format with separate date and time
      if (date) {
        const start = this.formatScheduledTime(startTime, date);
        const end = this.formatScheduledTime(endTime, date);
        return `${start} - ${end}`;
      }

      // Fallback: format as simple time strings
      const start = this.formatTime12Hour(startTime);
      const end = this.formatTime12Hour(endTime);
      return `${start} - ${end}`;
    },

    formatStatus(status) {
      if (!status) return "";

      // Handle specific status mappings (same as Shifts.vue)
      const statusMap = {
        "unassigned": "Unassigned",
        "scheduled": "Scheduled",
        "clocked-in": "Clocked In",
        "clocked-out": "Clocked Out",
        "approved": "Approved",
        "missed": "Missed",
        "error": "Error",
        "canceled": "Canceled"
      };

      return statusMap[status.toLowerCase()] || status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ");
    },

    formatClient(shift) {
      if (!shift) return "";
      const clients = shift.clients || shift.originalData?.clients || [];
      if (clients.length > 0) {
        // Show all clients, comma-separated
        return clients.map(client => {
          const name = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim();
          return name || "Unnamed Client";
        }).join(', ');
      }
      return shift.client || "Unassigned";
    },

    formatCaregiver(shift) {
      if (!shift) return "";
      const caregivers = shift.caregivers || shift.originalData?.caregivers || [];
      if (caregivers.length > 0) {
        // Show all caregivers, comma-separated
        return caregivers.map(caregiver => {
          const name = caregiver.name || `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim();
          return name || "Unnamed Caregiver";
        }).join(', ');
      }
      return shift.caregiver || "Unassigned";
    },

    formatActualTimeRange(shift) {
      // Handle both snake_case and camelCase formats from database
      const dbClockIn = shift.originalData?.clock_in || shift.originalData?.clockIn || shift.clockIn || shift.clock_in;
      const dbClockOut = shift.originalData?.clock_out || shift.originalData?.clockOut || shift.clockOut || shift.clock_out;

      // Handle processed times from Calendar (already in HH:MM format)
      const processedClockIn = shift.actualStartTime;
      const processedClockOut = shift.actualEndTime;

      if (!dbClockIn && !dbClockOut && !processedClockIn && !processedClockOut) {
        return "Not clocked in";
      }

      let clockInTime = "";
      let clockOutTime = "";

      // Format clock in time
      if (processedClockIn && processedClockIn.includes(":")) {
        // Already in HH:MM format from Calendar
        clockInTime = this.formatTime12Hour(processedClockIn);
      } else if (dbClockIn) {
        // Full datetime from database
        clockInTime = this.formatClockInTime(dbClockIn);
      }

      // Format clock out time
      if (processedClockOut && processedClockOut.includes(":")) {
        // Already in HH:MM format from Calendar
        clockOutTime = this.formatTime12Hour(processedClockOut);
      } else if (dbClockOut) {
        // Full datetime from database
        clockOutTime = this.formatClockOutTime(dbClockOut);
      }

      if (clockInTime && clockOutTime) {
        return `${clockInTime} - ${clockOutTime}`;
      } else if (clockInTime) {
        return `${clockInTime} - In Progress`;
      } else {
        return "Not clocked in";
      }
    },

    calculateHoursWorked(shift) {
      const dbClockIn = shift.originalData?.clock_in || shift.originalData?.clockIn || shift.clockIn || shift.clock_in;
      const dbClockOut = shift.originalData?.clock_out || shift.originalData?.clockOut || shift.clockOut || shift.clock_out;
      const processedClockIn = shift.actualStartTime;
      const processedClockOut = shift.actualEndTime;

      let clockInDate, clockOutDate;

      if (dbClockIn && dbClockOut) {
        clockInDate = new Date(dbClockIn);
        clockOutDate = new Date(dbClockOut);
      } else if (processedClockIn && processedClockOut) {
        // HH:MM format — calculate directly
        const [inH, inM] = processedClockIn.split(':').map(Number);
        const [outH, outM] = processedClockOut.split(':').map(Number);
        let totalMin = (outH * 60 + outM) - (inH * 60 + inM);
        if (totalMin < 0) totalMin += 24 * 60;
        const hrs = Math.floor(totalMin / 60);
        const mins = totalMin % 60;
        return `${hrs} hour${hrs !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
      } else {
        return 'N/A';
      }

      if (isNaN(clockInDate.getTime()) || isNaN(clockOutDate.getTime())) return 'N/A';

      let diffMs = clockOutDate.getTime() - clockInDate.getTime();
      if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000; // overnight

      const totalMin = Math.floor(diffMs / (1000 * 60));
      const hrs = Math.floor(totalMin / 60);
      const mins = totalMin % 60;

      if (hrs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
      if (mins === 0) return `${hrs} hour${hrs !== 1 ? 's' : ''}`;
      return `${hrs} hour${hrs !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
    },

    calculateHoursScheduled(shift) {
      const startTime = shift.scheduledStartTime || shift.originalData?.scheduled_start || shift.startTime;
      const endTime = shift.scheduledEndTime || shift.originalData?.scheduled_end || shift.endTime;

      if (!startTime || !endTime) return 'N/A';

      // Handle full ISO datetime strings
      if (startTime.includes('T')) {
        const startDate = new Date(startTime);
        const endDate = new Date(endTime);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) return 'N/A';
        let diffMs = endDate.getTime() - startDate.getTime();
        if (diffMs < 0) diffMs += 24 * 60 * 60 * 1000;
        const totalMin = Math.floor(diffMs / (1000 * 60));
        const hrs = Math.floor(totalMin / 60);
        const mins = totalMin % 60;
        if (hrs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
        if (mins === 0) return `${hrs} hour${hrs !== 1 ? 's' : ''}`;
        return `${hrs} hour${hrs !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
      }

      // Handle HH:MM format
      const [sH, sM] = startTime.split(':').map(Number);
      const [eH, eM] = endTime.split(':').map(Number);
      let totalMin = (eH * 60 + eM) - (sH * 60 + sM);
      if (totalMin < 0) totalMin += 24 * 60;
      const hrs = Math.floor(totalMin / 60);
      const mins = totalMin % 60;
      if (hrs === 0) return `${mins} minute${mins !== 1 ? 's' : ''}`;
      if (mins === 0) return `${hrs} hour${hrs !== 1 ? 's' : ''}`;
      return `${hrs} hour${hrs !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
    },

    getStatusClass(status) {
      if (!status) return "badge-secondary";

      // Handle different status formats
      const normalizedStatus = status.toLowerCase().replace(/\s+/g, "-");

      switch (normalizedStatus) {
        case "unassigned":
          return "badge-secondary";
        case "scheduled":
          return "badge-info";
        case "clocked-in":
        case "clocked in":
          return "badge-success";
        case "clocked-out":
        case "clocked out":
          return "badge-warning";
        case "approved":
          return "badge-primary";
        case "available":
          return "badge-success"; // Use success color for available
        case "missed":
          return "badge-secondary"; // Use secondary for missed
        case "error":
          return "badge-danger"; // Use danger (red) for error status
        case "canceled":
          return "badge-canceled";
        default:
          return "badge-info"; // Default to scheduled style
      }
    },

    hasCompletedBreaks(shift) {
      if (!shift) return false;
      const breaks = shift.breaks || shift.originalData?.breaks || [];
      return breaks.some(b => b.break_start && b.break_end);
    },

    formatBreakTimeRange(shift) {
      if (!shift) return "";
      const breaks = shift.breaks || shift.originalData?.breaks || [];
      const completedBreak = breaks.find(b => b.break_start && b.break_end);

      if (!completedBreak) return "";

      const breakStart = this.formatClockInTime(completedBreak.break_start);
      const breakEnd = this.formatClockOutTime(completedBreak.break_end);

      if (breakStart && breakEnd) {
        return `${breakStart} - ${breakEnd}`;
      }
      return "";
    },
  },
};
</script>

<style scoped>
.shift-action-content {
  max-width: 600px;
  min-width: 500px;
  margin: 0 auto;
  padding: 0 2rem;
}

.shift-action-content .mb-3 {
  margin-bottom: 1.5rem;
}

.shift-action-content h5 {
  color: #fff;
  margin-bottom: 1rem;
  font-weight: 600;
  border-bottom: 2px solid #6ba6cd;
  padding-bottom: 0.5rem;
  font-size: 1.1rem;
}

.shift-action-content p {
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.shift-action-content strong {
  color: #fff;
  font-weight: 600;
}

.cancel-reason-box {
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  background: rgba(75, 50, 30, 0.2);
  border-left: 3px solid rgba(75, 50, 30, 0.9);
  border-radius: 4px;
}

.cancel-reason-box strong {
  color: rgba(180, 140, 100, 1);
}

.cancel-reason-text {
  color: #e0e0e0;
  white-space: pre-wrap;
}

.text-center {
  text-align: center;
}

/* Status badge styling matching Shifts page table */
.status-badge {
  display: inline-block !important;
  padding: 0.35rem 0.75rem !important;
  font-size: 0.85rem !important;
  font-weight: 500 !important;
  border-radius: 1rem !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
}

/* Status badge colors matching Shifts page table */
.status-badge.badge-secondary {
  /* Unassigned - Teal */
  background: linear-gradient(
    135deg,
    rgba(32, 201, 151, 0.7),
    rgba(32, 201, 151, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-info {
  /* Scheduled - Bright blue */
  background: linear-gradient(
    135deg,
    rgba(13, 110, 253, 0.8),
    rgba(13, 110, 253, 0.6)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-success {
  /* Clocked In - Bright yellow */
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ) !important;
  color: #000 !important;
}

.status-badge.badge-warning {
  /* Clocked Out - Muted orange */
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-primary {
  /* Approved - Muted deep purple */
  background: linear-gradient(
    135deg,
    rgba(156, 39, 176, 0.7),
    rgba(156, 39, 176, 0.5)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-danger {
  /* Error - Red */
  background: linear-gradient(
    135deg,
    rgba(220, 53, 69, 0.8),
    rgba(220, 53, 69, 0.6)
  ) !important;
  color: #fff !important;
}

.status-badge.badge-canceled {
  /* Canceled - Dark brown-black */
  background: linear-gradient(
    135deg,
    rgba(75, 50, 30, 0.9),
    rgba(75, 50, 30, 0.7)
  ) !important;
  color: #fff !important;
}

/* Clock In button - Matches the bright yellow "Clocked In" status color */
.clock-in-btn {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 0.9),
    rgba(255, 193, 7, 0.7)
  ) !important;
  border: 1px solid rgba(0, 0, 0, 0.2) !important;
  color: #000 !important;
}

.clock-in-btn:hover {
  background: linear-gradient(
    135deg,
    rgba(255, 193, 7, 1),
    rgba(255, 193, 7, 0.8)
  ) !important;
  border: 1px solid rgba(0, 0, 0, 0.3) !important;
  color: #000 !important;
}

/* Clock Out button - Matches the muted orange color used elsewhere */
.clock-out-btn {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.7),
    rgba(237, 137, 54, 0.5)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}

.clock-out-btn:hover {
  background: linear-gradient(
    135deg,
    rgba(237, 137, 54, 0.8),
    rgba(237, 137, 54, 0.6)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

/* Break button - Teal/Cyan color to distinguish from Clock Out */
.break-btn {
  background: linear-gradient(
    135deg,
    rgba(23, 162, 184, 0.8),
    rgba(23, 162, 184, 0.6)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}

.break-btn:hover {
  background: linear-gradient(
    135deg,
    rgba(23, 162, 184, 0.9),
    rgba(23, 162, 184, 0.7)
  ) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #fff !important;
}

@media (max-width: 768px) {
  .shift-action-content {
    min-width: auto !important;
    padding: 0 1rem;
  }
}

.shift-notes-section {
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.shift-questions-section {
  margin-top: 0.75rem;
  margin-bottom: 0.5rem;
  color: #e0e0e0;
  font-size: 0.95rem;
}

.shift-questions-section strong {
  display: block;
  margin-bottom: 0.4rem;
}

.shift-questions-section i {
  color: #6BA6CD;
  margin-right: 0.35rem;
}

.shift-question-item {
  margin-top: 0.4rem;
  background: rgba(107, 166, 205, 0.1);
  border: 1px solid rgba(107, 166, 205, 0.25);
  border-radius: 6px;
  padding: 0.5rem 0.8rem;
}

.shift-question-text {
  font-weight: 600;
  color: #cfe3f0;
  font-size: 0.85rem;
}

.shift-question-answer {
  margin-top: 0.2rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: #e0e0e0;
  font-size: 0.9rem;
  line-height: 1.45;
}

.shift-notes-section strong {
  color: #fff;
  font-weight: 600;
}

.shift-notes-section i {
  color: #6BA6CD;
  margin-right: 0.35rem;
}

.shift-notes-content {
  margin-top: 0.4rem;
  background: rgba(107, 166, 205, 0.1);
  border: 1px solid rgba(107, 166, 205, 0.25);
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
  white-space: pre-wrap;
  word-break: break-word;
  color: #e0e0e0;
  font-size: 0.9rem;
  line-height: 1.45;
}

.map-link-btn {
  color: #6BA6CD !important;
  padding: 0 !important;
  font-size: 0.85rem;
}

.map-link-btn:hover {
  color: #8BC4E0 !important;
}
</style>
